import { useCallback, useEffect, useState } from "react";
import { Search, Sparkles, FileText, Bell, Loader2, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import "./ExecutiveOperations.css";

/**
 * Executive Intelligence tabs — mounted inside ExecutiveOperations.
 * Provides: Universal Search, AI Copilot, Executive Briefs, Aggregated
 * Notifications.
 */
export default function ExecutiveIntelligence() {
  const [tab, setTab] = useState("search");
  return (
    <div className="hw-eoc__widget" data-testid="executive-intelligence"
          style={{ marginTop: "0.9rem" }}>
      <h3>Executive Intelligence</h3>
      <div className="hw-eoc__nav" style={{ marginBottom: "0.6rem" }}>
        <button className={`hw-eoc__nav-btn ${tab === "search" ? "hw-eoc__nav-btn--active" : ""}`}
                onClick={() => setTab("search")}
                data-testid="ei-tab-search">
          <Search className="w-3.5 h-3.5"
                    style={{ display: "inline", verticalAlign: "text-bottom",
                              marginRight: "0.3rem" }} /> Search
        </button>
        <button className={`hw-eoc__nav-btn ${tab === "copilot" ? "hw-eoc__nav-btn--active" : ""}`}
                onClick={() => setTab("copilot")}
                data-testid="ei-tab-copilot">
          <Sparkles className="w-3.5 h-3.5"
                      style={{ display: "inline", verticalAlign: "text-bottom",
                                marginRight: "0.3rem" }} /> Copilot
        </button>
        <button className={`hw-eoc__nav-btn ${tab === "briefs" ? "hw-eoc__nav-btn--active" : ""}`}
                onClick={() => setTab("briefs")}
                data-testid="ei-tab-briefs">
          <FileText className="w-3.5 h-3.5"
                      style={{ display: "inline", verticalAlign: "text-bottom",
                                marginRight: "0.3rem" }} /> Briefs
        </button>
        <button className={`hw-eoc__nav-btn ${tab === "notifications" ? "hw-eoc__nav-btn--active" : ""}`}
                onClick={() => setTab("notifications")}
                data-testid="ei-tab-notifications">
          <Bell className="w-3.5 h-3.5"
                  style={{ display: "inline", verticalAlign: "text-bottom",
                            marginRight: "0.3rem" }} /> Alerts inbox
        </button>
      </div>
      {tab === "search" && <SearchPane />}
      {tab === "copilot" && <CopilotPane />}
      {tab === "briefs" && <BriefsPane />}
      {tab === "notifications" && <NotificationsPane />}
    </div>
  );
}

/* ---------------- Universal Search ---------------- */
function SearchPane() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const run = async () => {
    if (q.trim().length < 2) return;
    setLoading(true); setError("");
    try {
      const { data } = await api.get(`/executive-ops/search?q=${encodeURIComponent(q)}`);
      setResults(data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: "0.4rem" }}>
        <input value={q} onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && run()}
                placeholder="Search clients, orgs, referrals, grants, alerts, reports…"
                data-testid="ei-search-input"
                style={{ flex: 1, padding: "0.55rem 0.8rem",
                          background: "rgba(15,23,42,0.6)",
                          border: "1px solid #1E293B",
                          borderRadius: 6, color: "#E2E8F0",
                          fontSize: "0.9rem" }} />
        <button className="hw-eoc__nav-btn hw-eoc__nav-btn--active"
                onClick={run} disabled={loading}
                data-testid="ei-search-go">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Search"}
        </button>
      </div>
      {error && <div style={{ color: "#FCA5A5" }} data-testid="ei-search-error">{error}</div>}
      {results && (
        <div style={{ marginTop: "0.7rem" }} data-testid="ei-search-results">
          <div style={{ color: "#94A3B8", fontSize: "0.8rem" }}>
            {results.total} results across {results.results.length} module(s)
          </div>
          {results.results.map((r) => (
            <div key={r.collection} className="hw-eoc__list-item"
                  style={{ flexDirection: "column", alignItems: "stretch",
                            marginTop: "0.4rem" }}
                  data-testid={`ei-search-module-${r.collection}`}>
              <div style={{ fontWeight: 700, color: "#F8FAFC" }}>
                {r.module} <small style={{ color: "#94A3B8" }}>({r.count})</small>
              </div>
              <ul style={{ marginTop: "0.35rem", paddingLeft: "1rem",
                             fontSize: "0.78rem", color: "#CBD5E1" }}>
                {r.hits.slice(0, 5).map((h, i) => (
                  <li key={i}>{JSON.stringify(h)}</li>
                ))}
              </ul>
            </div>
          ))}
          {results.results.length === 0 && (
            <div className="hw-eoc__empty">No results for &quot;{results.query}&quot;.</div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------------- Copilot ---------------- */
function CopilotPane() {
  const [q, setQ] = useState("");
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/executive-ops/copilot/history?limit=20");
      setHistory(data.history || []);
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const ask = async () => {
    if (q.trim().length < 3) return;
    setAsking(true); setError("");
    try {
      const { data } = await api.post("/executive-ops/copilot/ask", { question: q });
      setAnswer(data); setQ("");
      await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setAsking(false); }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: "0.4rem" }}>
        <input value={q} onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && ask()}
                placeholder="Ask your Copilot… e.g. Which counties need attention?"
                data-testid="ei-copilot-input"
                style={{ flex: 1, padding: "0.55rem 0.8rem",
                          background: "rgba(15,23,42,0.6)",
                          border: "1px solid #1E293B",
                          borderRadius: 6, color: "#E2E8F0",
                          fontSize: "0.9rem" }} />
        <button className="hw-eoc__nav-btn hw-eoc__nav-btn--active"
                onClick={ask} disabled={asking}
                data-testid="ei-copilot-ask">
          {asking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
                     (<><Send className="w-3.5 h-3.5"
                                 style={{ verticalAlign: "text-bottom", marginRight: "0.25rem" }} />
                        Ask</>)}
        </button>
      </div>
      {error && <div style={{ color: "#FCA5A5" }} data-testid="ei-copilot-error">{error}</div>}
      {answer && (
        <div className="hw-eoc__list-item"
              style={{ flexDirection: "column", alignItems: "stretch",
                        marginTop: "0.5rem", whiteSpace: "pre-wrap",
                        color: "#F8FAFC", fontSize: "0.85rem" }}
              data-testid="ei-copilot-answer">
          <div style={{ fontSize: "0.7rem", color: "#94A3B8",
                          textTransform: "uppercase", letterSpacing: "0.12em" }}>
            Copilot · {answer.model}
          </div>
          <div>{answer.answer}</div>
        </div>
      )}
      {history.length > 0 && (
        <div style={{ marginTop: "0.7rem" }} data-testid="ei-copilot-history">
          <div style={{ color: "#94A3B8", fontSize: "0.75rem" }}>Recent questions</div>
          {history.slice(0, 5).map((h) => (
            <div key={h.id} className="hw-eoc__list-item">
              <span>{h.question}</span>
              <small>{(h.asked_at || "").slice(0, 16).replace("T", " ")}</small>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Briefs ---------------- */
const BRIEF_LABELS = {
  daily_executive_brief: "Daily Executive Brief",
  weekly_executive_summary: "Weekly Executive Summary",
  monthly_operations_review: "Monthly Operations Review",
  monthly_community_impact_report: "Monthly Community Impact",
  quarterly_board_packet: "Quarterly Board Packet",
  grant_performance_report: "Grant Performance",
  security_summary: "Security Summary",
  organization_health_report: "Organization Health",
};

function BriefsPane() {
  const [catalog, setCatalog] = useState([]);
  const [history, setHistory] = useState([]);
  const [generating, setGenerating] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [c, h] = await Promise.all([
        api.get("/executive-ops/briefs/catalog"),
        api.get("/executive-ops/briefs/history"),
      ]);
      setCatalog(c.data.kinds || []);
      setHistory(h.data.briefs || []);
    } catch (e) { setError(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async (kind) => {
    setGenerating(kind); setError("");
    try {
      const { data } = await api.post("/executive-ops/briefs/generate", { kind });
      await load();
      download(data.id, "pdf");
    } catch (e) { setError(formatApiError(e)); }
    finally { setGenerating(null); }
  };

  const download = (id, format) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/executive-ops/briefs/${id}.${format}?_t=${Date.now()}`;
    const token = localStorage.getItem("hw_access_token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const objUrl = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = objUrl; a.download = `brief.${format}`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(objUrl);
      });
  };

  return (
    <div>
      {error && <div style={{ color: "#FCA5A5" }} data-testid="ei-briefs-error">{error}</div>}
      <div className="hw-eoc__kpi-grid" data-testid="ei-briefs-catalog">
        {catalog.map((k) => (
          <div key={k} className="hw-eoc__kpi-tile" data-testid={`ei-brief-card-${k}`}>
            <div className="hw-eoc__kpi-label">{BRIEF_LABELS[k] || k}</div>
            <button className="hw-eoc__nav-btn hw-eoc__nav-btn--active"
                    onClick={() => generate(k)}
                    disabled={generating === k}
                    data-testid={`ei-brief-generate-${k}`}
                    style={{ marginTop: "0.5rem", width: "100%" }}>
              {generating === k ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : "Generate + PDF"}
            </button>
          </div>
        ))}
      </div>
      {history.length > 0 && (
        <div style={{ marginTop: "0.7rem" }} data-testid="ei-briefs-history">
          <div style={{ color: "#94A3B8", fontSize: "0.75rem",
                          marginBottom: "0.3rem" }}>Recent briefs</div>
          <table className="hw-eoc__table">
            <thead>
              <tr>
                <th>Kind</th><th>Generated</th><th></th>
              </tr>
            </thead>
            <tbody>
              {history.slice(0, 10).map((h) => (
                <tr key={h.id} data-testid={`ei-brief-row-${h.id}`}>
                  <td>{BRIEF_LABELS[h.kind] || h.kind}</td>
                  <td style={{ fontSize: "0.75rem", color: "#94A3B8" }}>
                    {(h.generated_at || "").slice(0, 16).replace("T", " ")}
                  </td>
                  <td style={{ display: "flex", gap: "0.3rem" }}>
                    <button className="hw-eoc__nav-btn"
                            onClick={() => download(h.id, "csv")}
                            data-testid={`ei-brief-csv-${h.id}`}>CSV</button>
                    <button className="hw-eoc__nav-btn hw-eoc__nav-btn--active"
                            onClick={() => download(h.id, "pdf")}
                            data-testid={`ei-brief-pdf-${h.id}`}>PDF</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------- Notifications ---------------- */
function NotificationsPane() {
  const [data, setData] = useState(null);
  const [sev, setSev] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const url = `/executive-ops/notifications?since_days=14${sev ? `&severity=${sev}` : ""}`;
      const { data } = await api.get(url);
      setData(data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  }, [sev]);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ display: "flex", gap: "0.4rem", marginBottom: "0.5rem" }}>
        <select value={sev} onChange={(e) => setSev(e.target.value)}
                data-testid="ei-notif-severity"
                style={{ background: "rgba(15,23,42,0.6)",
                          border: "1px solid #1E293B", color: "#E2E8F0",
                          padding: "0.3rem 0.5rem", borderRadius: 6 }}>
          <option value="">All severities</option>
          <option value="critical">Critical</option>
          <option value="warning">Warning</option>
          <option value="info">Info</option>
        </select>
        <button className="hw-eoc__nav-btn" onClick={load}
                data-testid="ei-notif-refresh">Refresh</button>
      </div>
      {error && <div style={{ color: "#FCA5A5" }} data-testid="ei-notif-error">{error}</div>}
      {loading && (
        <div style={{ color: "#94A3B8" }}>
          <Loader2 className="w-4 h-4 animate-spin"
                    style={{ verticalAlign: "text-bottom" }} /> loading…
        </div>
      )}
      {data && (
        <>
          <div style={{ color: "#94A3B8", fontSize: "0.8rem",
                          marginBottom: "0.4rem" }}
                data-testid="ei-notif-counts">
            Critical {data.counts_by_severity.critical} ·
            Warning {data.counts_by_severity.warning} ·
            Info {data.counts_by_severity.info}
          </div>
          <div className="hw-eoc__timeline" data-testid="ei-notif-list">
            {data.notifications.map((n) => (
              <div key={n.id}
                    className={`hw-eoc__timeline-item hw-eoc__timeline-item--${n.severity || "info"}`}
                    data-testid={`ei-notif-${n.id}`}>
                <div className="hw-eoc__timeline-meta">
                  <span>{n.source.replace(/_/g, " ")}</span>
                  <span>·</span>
                  <span>{(n.at || "").slice(0, 16).replace("T", " ")}</span>
                </div>
                <div className="hw-eoc__timeline-title">{n.title}</div>
                {n.body && <div className="hw-eoc__timeline-body">{n.body.slice(0, 200)}</div>}
              </div>
            ))}
            {data.notifications.length === 0 && (
              <div className="hw-eoc__empty">No notifications in this window.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
